const DSA_SELECTOR = '[data-doto-smart-addon]';

const normalize = (value = '') => String(value)
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .toLowerCase()
  .trim();

const routeRoot = () => window.Shopify?.routes?.root || window.dynamicURLs?.root || '/';

const formatMoney = (cents) => {
  if (window.Shopify?.formatMoney) {
    return window.Shopify.formatMoney(cents, window.Shopify.money_format);
  }
  return new Intl.NumberFormat('es-MX', {
    style: 'currency', currency: 'MXN', minimumFractionDigits: 2, maximumFractionDigits: 2
  }).format(Number(cents || 0) / 100);
};

const uniqueById = (items) => {
  const seen = new Set();
  return items.filter((item) => {
    const id = Number(item?.product?.id || item?.id);
    if (!id || seen.has(id)) return false;
    seen.add(id);
    return true;
  });
};

const datasetCandidate = (node) => {
  if (!node) return null;
  return {
    source: node.dataset.source || 'liquid',
    product: {
      id: Number(node.dataset.productId),
      handle: node.dataset.handle || '',
      vendor: node.dataset.vendor || '',
      title: node.dataset.title || '',
      type: node.dataset.type || '',
      product_type: node.dataset.type || '',
      tags: String(node.dataset.tags || '').split(',').map((v) => v.trim()).filter(Boolean),
      featured_image: node.dataset.image || '',
      variants: [{
        id: Number(node.dataset.variantId),
        available: true,
        price: Number(node.dataset.price),
        compare_at_price: Number(node.dataset.compareAtPrice || 0),
        featured_image: node.dataset.image || ''
      }]
    }
  };
};

class DotoSmartAddon {
  constructor(root) {
    this.root = root;
    this.view = root.querySelector('[data-dsa-view]');
    this.status = root.querySelector('[data-dsa-status]');
    this.button = root.querySelector('[data-dsa-add]');
    this.pending = false;

    this.cfg = {
      productId: Number(root.dataset.productId),
      productHandle: String(root.dataset.productHandle || ''),
      vendorRaw: String(root.dataset.productVendor || '').trim(),
      vendor: normalize(root.dataset.productVendor),
      currentPrice: Number(root.dataset.currentPrice || 0),
      idealRatio: Number(root.dataset.idealRatio || 0.35),
      fallbackRatio: Number(root.dataset.fallbackRatio || 0.55),
      allowFallback: root.dataset.allowFallback !== 'false',
      requireSameVendor: root.dataset.requireSameVendor !== 'false',
      showCompare: root.dataset.showCompare === 'true',
      keywords: String(root.dataset.accessoryKeywords || '')
        .split(/[,\n]/)
        .map(normalize)
        .filter(Boolean),
      buttonLabel: root.dataset.buttonLabel || 'Agregar al carrito',
      addedLabel: root.dataset.addedLabel || 'Agregado ✓'
    };

    this.cfg.fallbackRatio = Math.max(this.cfg.idealRatio, this.cfg.fallbackRatio);
  }

  async init() {
    if (this.root.dataset.dsaInitialized === 'true') return;
    this.root.dataset.dsaInitialized = 'true';
    this.button?.addEventListener('click', this.addToCart.bind(this));

    if (!this.cfg.productId || !this.cfg.currentPrice || !this.cfg.vendor) {
      this.finishEmpty('Faltan datos del producto principal.');
      return;
    }

    try {
      const liquid = [...this.root.querySelectorAll('[data-dsa-liquid-candidate]')]
        .map(datasetCandidate)
        .filter(Boolean);

      const manual = datasetCandidate(this.root.querySelector('[data-dsa-manual-fallback]'));

      this.setStatus(`Buscando complemento para ${this.cfg.vendorRaw}…`);

      const complementary = await this.fetchRecommendations('complementary');
      const related = await this.fetchRecommendations('related');

      let pool = uniqueById([
        ...liquid,
        ...complementary.map((product) => ({ source: 'recommendations-complementary', product })),
        ...related.map((product) => ({ source: 'recommendations-related', product }))
      ]);

      let best = this.pickBest(pool);

      if (!best) {
        const searched = await this.searchByVendorAndKeywords();
        pool = uniqueById([...pool, ...searched]);
        best = this.pickBest(pool);
      }

      if (!best && manual) {
        best = this.pickBest([manual], { ignoreAccessory: true, ignoreRatio: true });
      }

      if (!best) {
        this.finishEmpty(`No encontré un complemento automático para ${this.cfg.vendorRaw}. Puedes elegir un producto de respaldo en el bloque.`);
        return;
      }

      this.render(best);
    } catch (error) {
      console.warn('[Doto Smart Add-on V2]', error);
      this.finishError('No se pudo cargar el complemento. Revisa la consola para más detalle.');
    }
  }

  setStatus(message) {
    if (this.status) this.status.textContent = message;
  }

  setState(state) {
    this.root.dataset.state = state;
  }

  finishEmpty(message) {
    this.setStatus(message);
    this.setState('empty');
  }

  finishError(message) {
    this.setStatus(message);
    this.setState('error');
  }

  async fetchRecommendations(intent) {
    try {
      const base = window.dynamicURLs?.productRecommendations || `${routeRoot()}recommendations/products`;
      const join = base.includes('?') ? '&' : '?';
      const url = `${base}.json${join}product_id=${encodeURIComponent(this.cfg.productId)}&limit=10&intent=${intent}`;
      const response = await fetch(url, { headers: { Accept: 'application/json' } });
      if (!response.ok) return [];
      const data = await response.json();
      return Array.isArray(data.products) ? data.products : [];
    } catch (_) {
      return [];
    }
  }

  orderedKeywords() {
    const priority = [
      'cargador', 'cable', 'adaptador', 'power bank', 'magsafe',
      'funda', 'case', 'carcasa', 'protector', 'mica', 'vidrio',
      'audifonos', 'earbuds', 'correa', 'strap', 'dock', 'base',
      'soporte', 'hub', 'stylus', 'pencil', 'teclado', 'mouse',
      'control', 'gamepad', 'bocina', 'soundbar', 'mochila', 'accesorio'
    ].map(normalize);

    const configured = this.cfg.keywords;
    const source = configured.length ? [...priority, ...configured] : priority;
    const seen = new Set();

    return source.filter((term) => {
      if (!term || seen.has(term)) return false;
      seen.add(term);
      return !configured.length || configured.includes(term);
    }).slice(0, 20);
  }

  async searchByVendorAndKeywords() {
    const results = [];
    const terms = this.orderedKeywords().map((keyword) => `${this.cfg.vendorRaw} ${keyword}`.trim());
    terms.push(this.cfg.vendorRaw);

    for (const term of terms) {
      this.setStatus(`Buscando: ${term}`);
      const found = await this.predictiveProducts(term);
      for (const product of found) {
        if (!product?.id || Number(product.id) === this.cfg.productId) continue;
        const full = await this.productJson(product.handle);
        if (full) results.push({ source: `search:${term}`, product: full });
      }

      const currentBest = this.pickBest(uniqueById(results));
      if (currentBest) return uniqueById(results);
    }

    return uniqueById(results);
  }

  async predictiveProducts(term) {
    if (!term) return [];
    try {
      const endpoint = `${routeRoot()}search/suggest.json?q=${encodeURIComponent(term)}&resources[type]=product&resources[limit]=10&resources[limit_scope]=each&resources[options][unavailable_products]=hide`;
      const response = await fetch(endpoint, { headers: { Accept: 'application/json' } });
      if (!response.ok) return [];
      const data = await response.json();
      return data?.resources?.results?.products || [];
    } catch (_) {
      return [];
    }
  }

  async productJson(handle) {
    if (!handle) return null;
    try {
      const response = await fetch(`${routeRoot()}products/${encodeURIComponent(handle)}.js`, {
        headers: { Accept: 'application/json' }
      });
      if (!response.ok) return null;
      return await response.json();
    } catch (_) {
      return null;
    }
  }

  cheapestAvailableVariant(product) {
    const variants = Array.isArray(product?.variants) ? product.variants : [];
    const available = variants.filter((variant) => variant && variant.available !== false && Number(variant.price) > 0);
    if (!available.length) return null;
    return available.reduce((best, variant) => Number(variant.price) < Number(best.price) ? variant : best);
  }

  isAccessory(product) {
    if (!this.cfg.keywords.length) return true;
    const haystack = normalize([
      product?.title,
      product?.product_type,
      product?.type,
      product?.body,
      ...(Array.isArray(product?.tags) ? product.tags : [])
    ].filter(Boolean).join(' '));
    return this.cfg.keywords.some((keyword) => haystack.includes(keyword));
  }

  pickBest(entries, options = {}) {
    const { ignoreAccessory = false, ignoreRatio = false } = options;

    return entries
      .map((entry) => {
        const product = entry?.product || entry;
        if (!product || Number(product.id) === this.cfg.productId) return null;
        if (this.cfg.requireSameVendor && normalize(product.vendor) !== this.cfg.vendor) return null;

        const variant = this.cheapestAvailableVariant(product);
        if (!variant) return null;

        const price = Number(variant.price);
        if (!price || price >= this.cfg.currentPrice) return null;

        const ratio = price / this.cfg.currentPrice;
        if (!ignoreRatio && ratio > this.cfg.fallbackRatio) return null;

        const accessory = ignoreAccessory ? true : this.isAccessory(product);
        if (!accessory && !this.cfg.allowFallback && !ignoreAccessory) return null;

        let score = 0;
        if (entry.source === 'search-discovery') score += 300;
        if (entry.source === 'recommendations-complementary') score += 220;
        if (String(entry.source || '').startsWith('search:')) score += 180;
        if (entry.source === 'recommendations-related') score += 90;
        if (entry.source === 'manual') score += 50;
        if (accessory) score += 120;
        if (ratio <= this.cfg.idealRatio) score += 60;
        score += Math.max(0, 40 - Math.round(ratio * 40));

        return { product, variant, price, score, source: entry.source || 'unknown' };
      })
      .filter(Boolean)
      .sort((a, b) => b.score - a.score || a.price - b.price)[0] || null;
  }

  imageUrl(product, variant) {
    const raw = variant?.featured_image?.src
      || variant?.featured_image?.url
      || variant?.featured_image
      || variant?.image
      || product?.featured_image?.src
      || product?.featured_image?.url
      || product?.featured_image
      || product?.image
      || product?.images?.[0];

    if (!raw) return '';
    return typeof raw === 'string' ? raw : (raw.src || raw.url || '');
  }

  render(candidate) {
    const { product, variant, price, source } = candidate;
    const image = this.root.querySelector('.doto-smart-addon__image');
    const imageUrl = this.imageUrl(product, variant);

    if (image && imageUrl) {
      image.src = imageUrl;
      image.alt = product.title || '';
      image.hidden = false;
    }

    const vendor = this.root.querySelector('[data-dsa-vendor]');
    const title = this.root.querySelector('[data-dsa-title]');
    const priceEl = this.root.querySelector('[data-dsa-price]');
    const compare = this.root.querySelector('[data-dsa-compare]');
    const cashback = this.root.querySelector('[data-dsa-cashback]');
    const cashbackAmount = this.root.querySelector('[data-dsa-cashback-amount]');

    if (vendor) vendor.textContent = product.vendor || '';
    if (title) title.textContent = product.title || '';
    if (priceEl) priceEl.textContent = formatMoney(price);

    const cashbackPesos = Math.round(Number(price || 0) / 10000);
    if (cashback && cashbackAmount && cashbackPesos > 0) {
      cashbackAmount.textContent = `$${cashbackPesos.toLocaleString('en-US')}`;
      cashback.setAttribute('aria-label', `Recibe $${cashbackPesos.toLocaleString('en-US')} en DotoCash`);
      cashback.hidden = false;
    }

    const comparePrice = Number(variant.compare_at_price || 0);
    if (compare && this.cfg.showCompare && comparePrice > price) {
      compare.textContent = formatMoney(comparePrice);
      compare.hidden = false;
    }

    if (this.button) {
      this.button.dataset.variantId = String(variant.id);
      this.button.disabled = false;
    }

    this.root.dataset.source = source;
    if (this.view) this.view.hidden = false;
    this.setState('ready');
  }

  async addToCart(event) {
    event.preventDefault();
    if (this.pending || !this.button) return;

    const variantId = Number(this.button.dataset.variantId);
    if (!variantId) return;

    const label = this.button.querySelector('[data-dsa-button-label]');
    this.pending = true;
    this.button.disabled = true;
    if (label) label.textContent = 'Agregando…';

    try {
      const endpoint = `${window.dynamicURLs?.cartAdd || `${routeRoot()}cart/add`}.js`;
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] })
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data?.description || data?.message || 'No se pudo agregar al carrito');

      this.root.classList.add('is-added');
      if (label) label.textContent = this.cfg.addedLabel;
      document.dispatchEvent(new CustomEvent('cart:refresh'));

      window.setTimeout(() => {
        this.root.classList.remove('is-added');
        if (label) label.textContent = this.cfg.buttonLabel;
      }, 1300);
    } catch (error) {
      console.error('[Doto Smart Add-on V2] cart', error);
      if (label) label.textContent = this.cfg.buttonLabel;
    } finally {
      this.pending = false;
      this.button.disabled = false;
    }
  }
}

const initSmartAddons = (scope = document) => {
  const roots = [];
  if (scope.matches?.(DSA_SELECTOR)) roots.push(scope);
  roots.push(...scope.querySelectorAll?.(DSA_SELECTOR) || []);
  roots.forEach((root) => new DotoSmartAddon(root).init());
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initSmartAddons(document), { once: true });
} else {
  initSmartAddons(document);
}

document.addEventListener('shopify:section:load', (event) => initSmartAddons(event.target));
document.addEventListener('shopify:block:select', (event) => initSmartAddons(event.target));

const observer = new MutationObserver((mutations) => {
  for (const mutation of mutations) {
    for (const node of mutation.addedNodes) {
      if (node instanceof HTMLElement) initSmartAddons(node);
    }
  }
});
observer.observe(document.documentElement, { childList: true, subtree: true });
